import { WagonFill18Icon } from "@vygruppen/spor-icon-react";
import { CancelButton } from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistCustomStyle";
import { FillOut } from "features/CenterContent/RoleContent/VehicleChecklist/schema/responseSchema";
import {
  formatDateTime,
  getChecklistText,
} from "features/CenterContent/RoleContent/VehicleChecklist/utils";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { FC, JSX, useState } from "react";
import {
  Box,
  DialogBody,
  DialogFooter,
  DropsButton,
  Flex,
  Text,
} from "shared/components/Spor";
import { useTheme } from "styled-components";

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

type ChecklistDetailProps = {
  label: string;
  value: string | JSX.Element;
};

const ChecklistDetail: FC<ChecklistDetailProps> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Flex pl="18px" pt="10px" gap="10px">
      <Text fontWeight="bold" color={theme.colorAlarmTextMain}>
        {label}:{" "}
      </Text>
      <Text color={theme.colorAlarmTextSecondary}>{value}</Text>
    </Flex>
  );
};

type DeleteChecklistModalContentProps = {
  currentFillOut: FillOut;
  onClose: () => void;
  handleDelete: () => void;
  refetch: () => void;
};

const DeleteChecklistModalContent: FC<DeleteChecklistModalContentProps> = ({
  currentFillOut,
  onClose,
  handleDelete,
  refetch,
}) => {
  const theme = useTheme();

  const [isDeleted, setIsDeleted] = useState(false);

  const handleDeleteClick = async () => {
    try {
      setIsDeleted(true);
      handleDelete();
      await wait(2000);
      setIsDeleted(false);
      refetch();
      onClose();
    } catch (error) {
      log(
        LogLevel.error,
        "DeleteChecklistModal.handleDeleteClick",
        "Error deleting checklist",
        error,
      );
    }
  };

  if (isDeleted) {
    return (
      <Text fontWeight={500} pl={30} pt={30} pb={30} borderBottomRadius="16px">
        Kvitteringen er slettet.
      </Text>
    );
  }

  if (!currentFillOut) {
    return (
      <Text fontWeight={500} pl={30} pt={30} pb={30}>
        Ingen kvittering er valgt.
      </Text>
    );
  }

  const formattedDateTime = `${formatDateTime(currentFillOut.executedAt.toString())}`;
  return (
    <>
      <DialogBody display="flex" flexDirection="column" gap="30px">
        <Text fontWeight={500} color={theme.colorAlarmTextMain}>
          Du er i ferd med å slette denne kvitteringen:
        </Text>
        <Box borderLeft={`2px solid ${theme.colorAlarmOutline}`}>
          <ChecklistDetail
            label="Kvittert av"
            value={currentFillOut.executorName || "N/A"}
          />
          <ChecklistDetail label="Tidspunkt" value={formattedDateTime} />
          <ChecklistDetail
            label="Kvitteringsliste"
            value={getChecklistText(currentFillOut.checklistType)}
          />
          <ChecklistDetail
            label="Kjøretøy"
            value={
              <Flex key={currentFillOut.rollingStock} alignItems="center">
                <WagonFill18Icon />
                <Text
                  pl="6px"
                  color={theme.colorAlarmTextSecondary}
                  fontWeight="600"
                >
                  {currentFillOut.rollingStock}
                </Text>
              </Flex>
            }
          />
        </Box>

        <Text fontWeight={500} color={theme.colorAlarmTextMain}>
          Er du sikker på at du vil slette?
        </Text>
      </DialogBody>
      <DialogFooter>
        <Flex justifyContent="space-between" width="100%">
          <CancelButton fontWeight={500} onClick={onClose} variant="tertiary">
            Avbryt
          </CancelButton>
          <DropsButton
            variant="primary"
            semantics="alarm"
            onClick={handleDeleteClick}
            style={{
              color: theme.colorAlarmTextTertiary,
            }}
          >
            Ja, slett kvittering
          </DropsButton>
        </Flex>
      </DialogFooter>
    </>
  );
};

export default DeleteChecklistModalContent;
